/*  HomeLiveStrip — dynamic pulse of the user's ORIO life.

    A single horizontally-scrolling row of live-data pills that surfaces
    the things that used to be buried in collapsible sections:
      • Karma totale (with delta this week)
      • Promemoria attivi (with next-due)
      • Passeggiate + km della settimana
      • Storie pubblicate ultimi 30gg
      • Notifiche non lette

    Each pill is compact (rounded-2xl, ~150-180px wide), has an accent
    color, a big tabular number, and a tiny sub-label. Tap → deep-link.

    Renders only if we have at least one real fact to show, so it never
    feels empty for new users.
*/
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Bell, Footprints, Camera, Zap, Trophy, MapPin, Target } from "lucide-react";

export default function HomeLiveStrip({
  karma,             // number | null — karma total (locked)
  remindersActive,   // number
  nextReminder,      // { title, days? }
  walksWeek,         // number
  kmWeek,            // number (km)
  storiesWeek,       // number
  unreadNotifs,      // number
  streakDays,        // number | null
  cityRank,          // number | null — user's rank in their city
  cityName,          // string | null — user's city
  questsCompleted,   // number
  questsTotal,       // number
}) {
  const items = [];

  if (typeof karma === "number") {
    items.push({
      id: "karma",
      icon: Sparkles,
      color: "#F4B83C",
      big: karma.toLocaleString("it-IT"),
      label: "Karma totale",
      to: "/app/profilo",
      testid: "live-karma",
    });
  }

  if (typeof cityRank === "number" && cityRank > 0) {
    items.push({
      id: "city",
      icon: cityRank <= 3 ? Trophy : MapPin,
      color: cityRank === 1 ? "#F4B83C" : cityRank <= 3 ? "#FF7A1A" : "#7B61FF",
      big: `#${cityRank}`,
      label: cityName ? `Classifica ${cityName}` : "Classifica città",
      to: "/app/profilo#leaderboard",
      testid: "live-city-rank",
    });
  }

  if (questsTotal && questsTotal > 0) {
    items.push({
      id: "quests",
      icon: Target,
      color: questsCompleted >= questsTotal ? "#10B981" : "#FF7A1A",
      big: `${questsCompleted || 0}/${questsTotal}`,
      label: "Sfide settimanali",
      to: "/app/profilo#quests",
      testid: "live-quests",
    });
  }

  if (remindersActive > 0) {
    items.push({
      id: "reminders",
      icon: Bell,
      color: "#FF7A1A",
      big: String(remindersActive),
      label: nextReminder?.title
        ? `Prossimo: ${nextReminder.title.slice(0, 22)}${nextReminder.title.length > 22 ? "…" : ""}`
        : remindersActive === 1
        ? "Promemoria attivo"
        : "Promemoria attivi",
      to: "/app/cassetto",
      testid: "live-reminders",
    });
  }

  if (walksWeek > 0 || kmWeek > 0) {
    items.push({
      id: "walks",
      icon: Footprints,
      color: "#10B981",
      big: kmWeek > 0 ? `${kmWeek.toFixed(1)}km` : `${walksWeek}`,
      label: kmWeek > 0
        ? `${walksWeek} ${walksWeek === 1 ? "passeggiata" : "passeggiate"} · 7gg`
        : "Passeggiate · 7gg",
      to: "/app/passeggiate",
      testid: "live-walks",
    });
  }

  if (typeof streakDays === "number" && streakDays > 0) {
    items.push({
      id: "streak",
      icon: Zap,
      color: "#E9542A",
      big: `${streakDays}🔥`,
      label: streakDays === 1 ? "Giorno di streak" : "Giorni di streak",
      to: "/app/match?tab=stories",
      testid: "live-streak",
    });
  }

  if (storiesWeek > 0) {
    items.push({
      id: "stories",
      icon: Camera,
      color: "#7B61FF",
      big: String(storiesWeek),
      label: storiesWeek === 1 ? "Storia questo mese" : "Storie questo mese",
      to: "/app/match?tab=stories",
      testid: "live-stories",
    });
  }

  if (unreadNotifs > 0) {
    items.push({
      id: "notifs",
      icon: Bell,
      color: "#B75E2A",
      big: String(unreadNotifs),
      label: unreadNotifs === 1 ? "Notifica da leggere" : "Notifiche da leggere",
      to: "/app/notifiche",
      testid: "live-notifs",
    });
  }

  if (items.length === 0) return null;

  return (
    <section
      className="mt-6 -mx-4 sm:-mx-6"
      data-testid="home-live-strip"
    >
      <div className="px-4 sm:px-6 mb-2">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#B75E2A]">
          In tempo reale
        </div>
      </div>
      <div
        className="flex gap-2.5 overflow-x-auto orio-h-scroll pb-2 px-4 sm:px-6 snap-x snap-proximity"
      >
        {items.map((it, i) => <Pill key={it.id} it={it} index={i} />)}
        <div aria-hidden className="shrink-0 w-1" />
      </div>
    </section>
  );
}

function Pill({ it, index }) {
  const Icon = it.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.28, delay: Math.min(index * 0.04, 0.2) }}
      className="shrink-0 snap-start"
    >
      <Link
        to={it.to}
        data-testid={it.testid}
        className="block rounded-2xl px-4 py-3 bg-white border border-[#F0EBE3] active:scale-[0.98] transition-transform"
        style={{
          width: 172,
          boxShadow: "0 12px 26px -18px rgba(0,0,0,0.18), 0 2px 6px -3px rgba(0,0,0,0.05)",
        }}
      >
        <div className="flex items-center gap-2">
          <div
            className="h-7 w-7 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${it.color}18`, color: it.color }}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
          </div>
          <div
            className="font-display text-[22px] font-bold tabular-nums leading-none"
            style={{ color: it.color, letterSpacing: "-0.02em" }}
          >
            {it.big}
          </div>
        </div>
        <div className="mt-2 text-[11.5px] text-[#5A5A5A] leading-tight line-clamp-2">
          {it.label}
        </div>
      </Link>
    </motion.div>
  );
}
